/**
 * Hall of Fame Desktop Template Scene
 * Uses exact desktop-hall-of-fame.html template from _ui_upgrade
 */

import BaseMenuScene from './BaseMenuScene.js';
import { auth, db } from '../firebaseConfig.js';
import { createSidebarNavigation } from '../components/SharedSidebarNavigation.js';
import { baseSceneIntegration } from '../services/BaseSceneIntegration.js';
import { addCriticalNavigationCSS } from '../utils/CriticalNavigationCSS.js';

export default class HallOfFameDesktopTemplate extends BaseMenuScene {
    constructor() {
        super('HallOfFameDesktopTemplate');
        this.leaderboardData = [];
        this.currentUser = null;
    }

    async create() {
        console.log('🏆 HallOfFameDesktopTemplate: Creating desktop Hall of Fame with exact template');
        
        // Call parent create
        super.create();
        
        try {
            // Load CSS first
            await this.loadTemplateCSS();
            
            // Create the interface
            this.createDesktopInterface();
            
            // Load and display data
            await this.loadHallOfFameData();
            
            console.log('🏆 HallOfFameDesktopTemplate: Desktop interface created successfully');
            
        } catch (error) {
            console.error('🏆 Error creating HallOfFameDesktopTemplate:', error);
        }
    }

    async loadTemplateCSS() {
        console.log('🎨 Loading Hall of Fame CSS...');
        
        // Load EXACT same CSS as Dashboard
        if (!document.getElementById('desktop-executive-dashboard-exact-css')) {
            console.log('🎨 Loading EXACT desktop template CSS...');
            const link = document.createElement('link');
            link.id = 'desktop-executive-dashboard-exact-css';
            link.rel = 'stylesheet';
            link.href = '/desktop-executive-dashboard-exact.css'; // NO query parameter
            link.onload = () => console.log('🎨 EXACT desktop template CSS loaded successfully');
            link.onerror = () => {
                console.error('🚨 EXACT desktop template CSS failed to load - loading fallback');
                // Load fallback CSS
                const fallbackLink = document.createElement('link');
                fallbackLink.rel = 'stylesheet';
                fallbackLink.href = '/glass-morphism-ui.css'; // NO query parameter
                document.head.appendChild(fallbackLink);
            };
            document.head.appendChild(link);
        } else {
            console.log('🎨 EXACT desktop template CSS already loaded');
        }
        
        // Load enterprise animation standards
        if (!document.getElementById('enterprise-animation-standards-css')) {
            const animationLink = document.createElement('link');
            animationLink.id = 'enterprise-animation-standards-css';
            animationLink.rel = 'stylesheet';
            animationLink.href = '/enterprise-animation-standards.css'; // NO query parameter
            document.head.appendChild(animationLink);
            console.log('🎨 Enterprise animation standards loaded');
        }
        
        // Add Hall of Fame content styling to match desktop theme
        this.addHallOfFameContentCSS();
        
        console.log('🎨 All Hall of Fame CSS loaded');
    }

    addHallOfFameContentCSS() {
        const contentStyle = document.createElement('style');
        contentStyle.id = 'hall-of-fame-content-css';
        contentStyle.textContent = `
            /* Hall of Fame Content Styling - Desktop Theme Consistent */
            
            /* Main Content Header */
            #auth-menu-dom-overlay .main-content {
                padding: 32px !important;
                background: var(--background) !important;
            }
            
            #auth-menu-dom-overlay .header {
                background: rgba(30, 41, 59, 0.6) !important;
                backdrop-filter: blur(10px) !important;
                border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
                padding: 0 32px !important;
                height: 70px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: space-between !important;
                margin: -32px -32px 32px -32px !important;
            }
            
            #auth-menu-dom-overlay .page-title {
                font-size: 28px !important;
                font-weight: 700 !important;
                color: rgba(255, 255, 255, 0.9) !important;
                margin-bottom: 8px !important;
            }
            
            #auth-menu-dom-overlay .breadcrumb {
                display: flex !important;
                align-items: center !important;
                gap: 8px !important;
                font-size: 14px !important;
                color: rgba(148, 163, 184, 1) !important;
            }
            
            #auth-menu-dom-overlay .breadcrumb-separator {
                color: rgba(148, 163, 184, 1) !important;
            }
            
            /* Fix scrolling */
            #auth-menu-dom-overlay .content-area {
                flex: 1 !important;
                overflow-y: auto !important;
                padding: 32px !important;
                height: calc(100vh - 70px) !important;
            }
            
            #auth-menu-dom-overlay .filter-tabs {
                display: flex !important;
                gap: 8px !important;
                background: rgba(255, 255, 255, 0.05) !important;
                border-radius: 10px !important;
                padding: 4px !important;
            }
            
            #auth-menu-dom-overlay .filter-tab {
                padding: 8px 16px !important;
                background: transparent !important;
                border: none !important;
                border-radius: 6px !important;
                color: rgba(255, 255, 255, 0.7) !important;
                font-weight: 600 !important;
                font-size: 13px !important;
                cursor: pointer !important;
                transition: all 0.3s ease !important;
            }
            
            #auth-menu-dom-overlay .filter-tab.active {
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%) !important;
                color: #0f172a !important;
            }
            
            #auth-menu-dom-overlay .time-filter {
                display: flex !important;
                gap: 8px !important;
            }
            
            #auth-menu-dom-overlay .time-button {
                padding: 8px 16px !important;
                background: rgba(255, 255, 255, 0.05) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
                border-radius: 8px !important;
                color: rgba(255, 255, 255, 0.9) !important;
                font-size: 13px !important;
                font-weight: 600 !important;
                cursor: pointer !important;
                transition: all 0.3s ease !important;
            }
            
            #auth-menu-dom-overlay .time-button.active {
                background: #00d4ff !important;
                border-color: #00d4ff !important;
                color: #0f172a !important;
            }
            
            /* Position Card */
            #auth-menu-dom-overlay .position-card {
                background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(14, 165, 233, 0.05) 100%) !important;
                border: 1px solid rgba(0, 212, 255, 0.2) !important;
                border-radius: 24px !important;
                padding: 32px !important;
                margin-bottom: 32px !important;
                display: grid !important;
                grid-template-columns: 1fr 2fr 1fr !important;
                gap: 48px !important;
                align-items: center !important;
            }
            
            #auth-menu-dom-overlay .rank-number {
                font-size: 64px !important;
                font-weight: 800 !important;
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%) !important;
                -webkit-background-clip: text !important;
                -webkit-text-fill-color: transparent !important;
                background-clip: text !important;
            }
            
            #auth-menu-dom-overlay .position-avatar {
                width: 80px !important;
                height: 80px !important;
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%) !important;
                border-radius: 50% !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                font-size: 32px !important;
                font-weight: 700 !important;
                color: #0f172a !important;
            }
            
            /* Podium Section */
            #auth-menu-dom-overlay .podium-section {
                margin-bottom: 48px !important;
            }
            
            #auth-menu-dom-overlay .podium-title {
                font-size: 24px !important;
                font-weight: 600 !important;
                margin-bottom: 32px !important;
                text-align: center !important;
                color: rgba(255, 255, 255, 0.9) !important;
            }
            
            #auth-menu-dom-overlay .podium-container {
                display: grid !important;
                grid-template-columns: repeat(3, 1fr) !important;
                gap: 24px !important;
            }
            
            #auth-menu-dom-overlay .podium-place {
                background: rgba(30, 41, 59, 0.6) !important;
                backdrop-filter: blur(20px) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
                border-radius: 20px !important;
                padding: 32px !important;
                text-align: center !important;
                position: relative !important;
                transition: all 0.3s ease !important;
            }
            
            /* Leaderboard Table */
            #auth-menu-dom-overlay .leaderboard-container {
                display: grid !important;
                grid-template-columns: 2fr 1fr !important;
                gap: 32px !important;
            }
            
            #auth-menu-dom-overlay .leaderboard-table {
                background: rgba(30, 41, 59, 0.6) !important;
                backdrop-filter: blur(20px) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
                border-radius: 20px !important;
                overflow: hidden !important;
            }
            
            #auth-menu-dom-overlay .table-header {
                padding: 20px 24px !important;
                border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
                display: flex !important;
                align-items: center !important;
                justify-content: space-between !important;
            }
            
            #auth-menu-dom-overlay .table-title {
                font-size: 18px !important;
                font-weight: 600 !important;
                color: rgba(255, 255, 255, 0.9) !important;
            }
            
            #auth-menu-dom-overlay .data-table {
                width: 100% !important;
                border-collapse: collapse !important;
            }
            
            #auth-menu-dom-overlay .data-table th {
                text-align: left !important;
                padding: 16px 24px !important;
                font-size: 12px !important;
                font-weight: 600 !important;
                color: rgba(148, 163, 184, 1) !important;
                text-transform: uppercase !important;
                background: rgba(255, 255, 255, 0.02) !important;
                border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
            }
            
            #auth-menu-dom-overlay .data-table td {
                padding: 16px 24px !important;
                font-size: 14px !important;
                border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
                color: rgba(255, 255, 255, 0.9) !important;
            }
            
            #auth-menu-dom-overlay .data-table tr:hover td {
                background: rgba(255, 255, 255, 0.03) !important;
            }
            
            #auth-menu-dom-overlay .user-avatar {
                width: 36px !important;
                height: 36px !important;
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%) !important;
                border-radius: 50% !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                font-size: 14px !important;
                font-weight: 600 !important;
                color: #0f172a !important;
            }
            
            #auth-menu-dom-overlay .score-cell {
                font-size: 16px !important;
                font-weight: 700 !important;
                color: #00d4ff !important;
            }
            
            /* Stats Panel */
            #auth-menu-dom-overlay .stats-panel {
                display: flex !important;
                flex-direction: column !important;
                gap: 24px !important;
            }
            
            #auth-menu-dom-overlay .stats-card {
                background: rgba(30, 41, 59, 0.6) !important;
                backdrop-filter: blur(20px) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
                border-radius: 20px !important;
                padding: 24px !important;
            }
            
            #auth-menu-dom-overlay .stats-title {
                font-size: 16px !important;
                font-weight: 600 !important;
                margin-bottom: 20px !important;
                color: rgba(255, 255, 255, 0.9) !important;
            }
            
            #auth-menu-dom-overlay .achievement-grid {
                display: grid !important;
                grid-template-columns: repeat(3, 1fr) !important;
                gap: 12px !important;
            }
            
            #auth-menu-dom-overlay .achievement {
                background: rgba(255, 255, 255, 0.03) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
                border-radius: 12px !important;
                padding: 16px !important;
                text-align: center !important;
                transition: all 0.3s ease !important;
            }
            
            #auth-menu-dom-overlay .achievement-icon {
                font-size: 32px !important;
                margin-bottom: 8px !important;
            }
            
            #auth-menu-dom-overlay .achievement-name {
                font-size: 11px !important;
                color: rgba(148, 163, 184, 1) !important;
            }
            
            /* Table search header */
            #auth-menu-dom-overlay .table-search-header {
                padding: 16px 24px !important;
                border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
            }
            
            #auth-menu-dom-overlay .search-box {
                width: 100% !important;
                padding: 8px 16px !important;
                background: rgba(255, 255, 255, 0.05) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
                border-radius: 8px !important;
                color: rgba(255, 255, 255, 0.9) !important;
                font-size: 14px !important;
            }
            
            /* Podium medal positioning */
            #auth-menu-dom-overlay .podium-medal {
                position: absolute !important;
                top: -20px !important;
                left: 50% !important;
                transform: translateX(-50%) !important;
                width: 48px !important;
                height: 48px !important;
                border-radius: 50% !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                font-size: 24px !important;
                font-weight: 700 !important;
                color: #0f172a !important;
            }
            
            #auth-menu-dom-overlay .podium-medal.gold {
                background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%) !important;
            }
            
            #auth-menu-dom-overlay .podium-medal.silver {
                background: linear-gradient(135deg, #94a3b8 0%, #64748b 100%) !important;
            }
            
            #auth-menu-dom-overlay .podium-medal.bronze {
                background: linear-gradient(135deg, #f97316 0%, #ea580c 100%) !important;
            }
            
            #auth-menu-dom-overlay .podium-avatar {
                width: 96px !important;
                height: 96px !important;
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%) !important;
                border-radius: 50% !important;
                margin: 0 auto 20px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                font-size: 36px !important;
                font-weight: 700 !important;
                color: #0f172a !important;
            }
            
            #auth-menu-dom-overlay .podium-name {
                font-size: 20px !important;
                font-weight: 700 !important;
                margin-bottom: 8px !important;
                color: rgba(255, 255, 255, 0.9) !important;
            }
            
            #auth-menu-dom-overlay .podium-company {
                font-size: 14px !important;
                color: rgba(255, 255, 255, 0.7) !important;
                margin-bottom: 20px !important;
            }
            
            #auth-menu-dom-overlay .podium-score {
                font-size: 32px !important;
                font-weight: 700 !important;
                color: #00d4ff !important;
                margin-bottom: 8px !important;
            }
            
            /* COMPREHENSIVE TEXT STYLING - EXACT Dashboard Standards */
            
            /* Position Card Typography */
            #auth-menu-dom-overlay .position-name {
                font-size: 24px !important;
                font-weight: 700 !important;
                margin-bottom: 8px !important;
                color: var(--text-primary) !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                letter-spacing: -0.5px !important;
            }
            
            #auth-menu-dom-overlay .position-meta {
                display: flex !important;
                gap: 24px !important;
                font-size: 14px !important;
                color: var(--text-secondary) !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
            }
            
            #auth-menu-dom-overlay .meta-item {
                display: flex !important;
                align-items: center !important;
                gap: 6px !important;
                font-weight: 500 !important;
            }
            
            #auth-menu-dom-overlay .position-stats {
                display: grid !important;
                grid-template-columns: repeat(3, 1fr) !important;
                gap: 24px !important;
            }
            
            #auth-menu-dom-overlay .stat {
                text-align: center !important;
            }
            
            #auth-menu-dom-overlay .stat-value {
                font-size: 28px !important;
                font-weight: 700 !important;
                color: var(--accent-primary) !important;
                margin-bottom: 4px !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                letter-spacing: -0.5px !important;
            }
            
            #auth-menu-dom-overlay .stat-label {
                font-size: 12px !important;
                color: var(--text-muted) !important;
                text-transform: uppercase !important;
                letter-spacing: 0.5px !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                font-weight: 600 !important;
            }
            
            #auth-menu-dom-overlay .rank-label {
                font-size: 14px !important;
                color: var(--text-muted) !important;
                text-transform: uppercase !important;
                letter-spacing: 0.5px !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                font-weight: 600 !important;
            }
            
            /* Podium Text Styling */
            #auth-menu-dom-overlay .podium-name {
                font-size: 20px !important;
                font-weight: 700 !important;
                margin-bottom: 8px !important;
                color: var(--text-primary) !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                letter-spacing: -0.5px !important;
            }
            
            #auth-menu-dom-overlay .podium-company {
                font-size: 14px !important;
                color: var(--text-secondary) !important;
                margin-bottom: 20px !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                font-weight: 500 !important;
            }
            
            #auth-menu-dom-overlay .podium-score {
                font-size: 32px !important;
                font-weight: 700 !important;
                color: var(--accent-primary) !important;
                margin-bottom: 8px !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                letter-spacing: -0.5px !important;
            }
            
            #auth-menu-dom-overlay .podium-label {
                font-size: 12px !important;
                color: var(--text-muted) !important;
                text-transform: uppercase !important;
                letter-spacing: 0.5px !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                font-weight: 600 !important;
            }
            
            /* Table Text Styling */
            #auth-menu-dom-overlay .table-title {
                font-size: 18px !important;
                font-weight: 600 !important;
                color: var(--text-primary) !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                letter-spacing: -0.3px !important;
            }
            
            #auth-menu-dom-overlay .data-table th {
                font-size: 12px !important;
                font-weight: 600 !important;
                color: var(--text-muted) !important;
                text-transform: uppercase !important;
                letter-spacing: 0.5px !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
            }
            
            #auth-menu-dom-overlay .data-table td {
                font-size: 14px !important;
                color: var(--text-primary) !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                font-weight: 500 !important;
            }
            
            #auth-menu-dom-overlay .user-name {
                font-weight: 600 !important;
                margin-bottom: 2px !important;
                color: var(--text-primary) !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
            }
            
            #auth-menu-dom-overlay .user-title {
                font-size: 12px !important;
                color: var(--text-muted) !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                font-weight: 500 !important;
            }
            
            #auth-menu-dom-overlay .score-cell {
                font-size: 16px !important;
                font-weight: 700 !important;
                color: var(--accent-primary) !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                letter-spacing: -0.3px !important;
            }
            
            /* Achievement Text Styling */
            #auth-menu-dom-overlay .stats-title {
                font-size: 16px !important;
                font-weight: 600 !important;
                margin-bottom: 20px !important;
                color: var(--text-primary) !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                letter-spacing: -0.3px !important;
            }
            
            #auth-menu-dom-overlay .achievement-name {
                font-size: 11px !important;
                color: var(--text-muted) !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                font-weight: 500 !important;
                text-transform: uppercase !important;
                letter-spacing: 0.3px !important;
            }
            
            /* Section Title Styling */
            #auth-menu-dom-overlay .podium-title {
                font-size: 24px !important;
                font-weight: 600 !important;
                margin-bottom: 32px !important;
                text-align: center !important;
                color: var(--text-primary) !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                letter-spacing: -0.5px !important;
            }
            
            /* Filter and Button Text */
            #auth-menu-dom-overlay .filter-tab {
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                font-weight: 600 !important;
                font-size: 13px !important;
                letter-spacing: 0.3px !important;
            }
            
            #auth-menu-dom-overlay .time-button {
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                font-weight: 600 !important;
                font-size: 13px !important;
                letter-spacing: 0.3px !important;
            }
        `;
        document.head.appendChild(contentStyle);
        console.log('🎨 Hall of Fame content CSS added');
    }


    createDesktopInterface() {
        console.log('🏆 Creating desktop Hall of Fame interface...');

        // CRITICAL: Add global critical CSS for all navigation
        addCriticalNavigationCSS();

        // Hide game container
        const gameContainer = document.getElementById('game-container');
        if (gameContainer) {
            gameContainer.style.display = 'none';
        }

        // Create overlay with EXACT same structure as Dashboard
        const overlay = document.createElement('div');
        overlay.id = 'auth-menu-dom-overlay';  // SAME ID as Dashboard
        overlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100vw;
            height: 100vh;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            z-index: 10000;
            overflow: hidden;
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: rgba(255, 255, 255, 0.9);
            box-sizing: border-box;
        `;

        // Add the exact HTML structure from desktop-hall-of-fame.html
        overlay.innerHTML = this.getDesktopHTML();

        // Add to document
        document.body.appendChild(overlay);

        // Bind events
        this.bindEvents();

        console.log('🏆 Desktop Hall of Fame interface created');
    }

    getDesktopHTML() {
        // EXACT same structure as Dashboard with Hall of Fame content
        return `
            <!-- Animated Background (SAME as Dashboard) -->
            <div class="background-effects">
                <div class="bg-grid"></div>
                <div class="bg-glow bg-glow-1"></div>
                <div class="bg-glow bg-glow-2"></div>
            </div>
            
            <!-- Layout Container (SAME as Dashboard) -->
            <div class="app-container">
                <!-- Sidebar Navigation (EXACT Dashboard structure) -->
                <aside class="sidebar">
                    <div class="sidebar-header">
                        <div class="logo">
                            <div class="logo-icon">D</div>
                            <div class="logo-text">
                                <div>DeckOps.ai</div>
                                <div class="logo-subtitle">Executive Platform</div>
                            </div>
                        </div>
                    </div>
                    
                    <nav class="nav-menu">
                        <div class="nav-section">
                            <div class="nav-section-title">Main</div>
                            <a href="#" class="nav-item" data-action="dashboard">
                                <span class="nav-icon">🏠</span>
                                <span class="nav-label">Dashboard</span>
                            </a>
                            <a href="#" class="nav-item" data-action="analytics">
                                <span class="nav-icon">📊</span>
                                <span class="nav-label">Analytics</span>
                            </a>
                            <a href="#" class="nav-item" data-action="ppt-hero-analytics">
                                <span class="nav-icon">📈</span>
                                <span class="nav-label">PPT Hero Analytics</span>
                                <span class="nav-badge">Detailed</span>
                            </a>
                            <a href="#" class="nav-item active" data-action="hall-of-fame">
                                <span class="nav-icon">🏆</span>
                                <span class="nav-label">Hall of Fame</span>
                                <span class="nav-badge">New</span>
                            </a>
                        </div>
                    
                        <div class="nav-section">
                            <div class="nav-section-title">Training Modules</div>
                            <a href="#" class="nav-item" data-action="game-center">
                                <span class="nav-icon">🎮</span>
                                <span class="nav-label">Game Center</span>
                            </a>
                            <a href="#" class="nav-item" data-game="ppt-hero">
                                <span class="nav-icon">📈</span>
                                <span class="nav-label">PPT Hero</span>
                            </a>
                            <a href="#" class="nav-item" data-game="philosophy-flow">
                                <span class="nav-icon">🎨</span>
                                <span class="nav-label">Philosophy Flow</span>
                            </a>
                            <a href="#" class="nav-item" data-game="slide-wars">
                                <span class="nav-icon">⚔️</span>
                                <span class="nav-label">Slide Wars</span>
                            </a>
                            <a href="#" class="nav-item" data-game="classify-tech">
                                <span class="nav-icon">🏛️</span>
                                <span class="nav-label">Classify Tech</span>
                            </a>
                        </div>
                        
                        <div class="nav-section">
                            <div class="nav-section-title">Settings</div>
                            <a href="#" class="nav-item" data-action="profile">
                                <span class="nav-icon">⚙️</span>
                                <span class="nav-label">Preferences</span>
                            </a>
                            <a href="#" class="nav-item" data-action="help">
                                <span class="nav-icon">❓</span>
                                <span class="nav-label">Help & Support</span>
                            </a>
                        </div>
                    </nav>
                    
                    <div class="sidebar-footer">
                        <div class="user-profile">
                            <div class="user-avatar">SH</div>
                            <div class="user-info">
                                <div class="user-name">Shane Hercules</div>
                                <div class="user-role">Executive</div>
                            </div>
                            <span style="color: var(--text-muted);">›</span>
                        </div>
                    </div>
                </aside>
                
                <!-- Main Content -->
                <main class="main-content">
                    <!-- Header with breadcrumb -->
                    <header class="header">
                        <div class="header-left">
                            <h1 class="page-title">Hall of Fame</h1>
                            <div class="breadcrumb">
                                <span>Home</span>
                                <span class="breadcrumb-separator">/</span>
                                <span>Hall of Fame</span>
                            </div>
                        </div>
                        <div class="header-right">
                            <div class="time-filter">
                                <button class="time-button" data-time="today">Today</button>
                                <button class="time-button active" data-time="week">This Week</button>
                                <button class="time-button" data-time="month">This Month</button>
                                <button class="time-button" data-time="all">All Time</button>
                            </div>
                        </div>
                    </header>
                    
                    <!-- Content Area -->
                    <div class="content-area">
                        <!-- Loading State -->
                        <div id="hall-of-fame-loading" class="loading-state" style="display: flex; justify-content: center; align-items: center; height: 200px; color: #00d4ff;">
                            <div>Loading Hall of Fame data...</div>
                        </div>
                        
                        <!-- Your Position -->
                        <div id="user-position-card" class="position-card" style="display: grid;">
                            <div class="position-rank">
                                <div class="rank-number" id="user-rank">#1</div>
                                <div class="rank-label">Your Ranking</div>
                            </div>
                            <div class="position-info">
                                <div class="position-avatar" id="user-avatar">SH</div>
                                <div class="position-details">
                                    <div class="position-name" id="user-name">Shane Hercules</div>
                                    <div class="position-meta">
                                        <span class="meta-item" id="user-company">🏢 Bain & Company</span>
                                        <span class="meta-item" id="user-location">📍 London</span>
                                        <span class="meta-item" id="user-title">💼 Executive</span>
                                    </div>
                                </div>
                            </div>
                            <div class="position-stats">
                                <div class="stat">
                                    <div class="stat-value" id="user-total-points">3,141</div>
                                    <div class="stat-label">Total Points</div>
                                </div>
                                <div class="stat">
                                    <div class="stat-value" id="user-win-rate">85%</div>
                                    <div class="stat-label">Win Rate</div>
                                </div>
                                <div class="stat">
                                    <div class="stat-value" id="user-games-played">47</div>
                                    <div class="stat-label">Games Played</div>
                                </div>
                            </div>
                        </div>
                        
                        <!-- Top 3 Podium -->
                        <section class="podium-section">
                            <h2 class="podium-title">Top Performers</h2>
                            <div class="podium-container" id="podium-container">
                                <!-- Podium will be populated dynamically -->
                            </div>
                        </section>
                        
                        <!-- Leaderboard Table & Stats -->
                        <div class="leaderboard-container">
                            <!-- Main Leaderboard -->
                            <div class="leaderboard-table">
                                <div class="table-header">
                                    <h3 class="table-title">Complete Rankings</h3>
                                    <div class="filter-tabs">
                                        <button class="filter-tab active" data-filter="company">Company</button>
                                        <button class="filter-tab" data-filter="global">Global</button>
                                        <button class="filter-tab" data-filter="friends">Friends</button>
                                        <button class="filter-tab" data-filter="games">Games</button>
                                    </div>
                                </div>
                                <div class="table-search-header">
                                    <input type="text" class="search-box" placeholder="Search players..." id="player-search">
                                </div>
                                
                                <table class="data-table">
                                    <thead>
                                        <tr>
                                            <th width="60">Rank</th>
                                            <th>Player</th>
                                            <th>Company</th>
                                            <th>Score</th>
                                            <th>Games</th>
                                            <th>Win Rate</th>
                                            <th>Change</th>
                                        </tr>
                                    </thead>
                                    <tbody id="leaderboard-table-body">
                                        <!-- Table rows will be populated dynamically -->
                                    </tbody>
                                </table>
                            </div>
                            
                            <!-- Stats Panel -->
                            <div class="stats-panel">
                                <!-- Achievements -->
                                <div class="stats-card">
                                    <h3 class="stats-title">Recent Achievements</h3>
                                    <div class="achievement-grid">
                                        <div class="achievement">
                                            <div class="achievement-icon">🏆</div>
                                            <div class="achievement-name">Champion</div>
                                        </div>
                                        <div class="achievement">
                                            <div class="achievement-icon">🔥</div>
                                            <div class="achievement-name">Hot Streak</div>
                                        </div>
                                        <div class="achievement">
                                            <div class="achievement-icon">⚡</div>
                                            <div class="achievement-name">Speed Demon</div>
                                        </div>
                                        <div class="achievement">
                                            <div class="achievement-icon">🎯</div>
                                            <div class="achievement-name">Perfect Score</div>
                                        </div>
                                        <div class="achievement">
                                            <div class="achievement-icon">💎</div>
                                            <div class="achievement-name">Elite Player</div>
                                        </div>
                                        <div class="achievement">
                                            <div class="achievement-icon">🌟</div>
                                            <div class="achievement-name">Rising Star</div>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Progress to Next Rank -->
                                <div class="stats-card">
                                    <h3 class="stats-title">Progress to Next Rank</h3>
                                    <div class="progress-section">
                                        <div class="progress-header">
                                            <span class="progress-title">Points Needed</span>
                                            <span class="progress-value" id="points-progress">- / -</span>
                                        </div>
                                        <div class="progress-bar">
                                            <div class="progress-fill" id="progress-fill" style="width: 0%;"></div>
                                        </div>
                                    </div>
                                    <div class="stat-list">
                                        <div class="stat-item">
                                            <span class="stat-name">Games to Play</span>
                                            <span class="stat-value" id="games-needed">~-</span>
                                        </div>
                                        <div class="stat-item">
                                            <span class="stat-name">Win Rate Required</span>
                                            <span class="stat-value" id="win-rate-needed">-%</span>
                                        </div>
                                        <div class="stat-item">
                                            <span class="stat-name">Time Estimate</span>
                                            <span class="stat-value" id="time-estimate">- days</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Company Rankings -->
                                <div class="stats-card">
                                    <h3 class="stats-title">Company Rankings</h3>
                                    <div class="stat-list" id="company-rankings">
                                        <!-- Company rankings will be populated dynamically -->
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
        `;
    }

    async loadHallOfFameData() {
        console.log('🏆 HallOfFameDesktopTemplate: Loading leaderboard data...');
        
        try {
            // Get current user info
            const user = auth.currentUser;
            if (user) {
                this.currentUser = {
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName || user.email?.split('@')[0] || 'User'
                };
            }

            // Load leaderboard data from Firebase
            const startTime = performance.now();
            
            // Query top 50 scores - using same query as working HallOfFameDOM
            const scoresRef = db.collection('scores')
                .where('game', '==', 'PPTHero')
                .orderBy('score', 'desc')
                .limit(50);
            
            const snapshot = await scoresRef.get();
            const loadTime = performance.now() - startTime;
            
            console.log(`🏆 HallOfFameDesktopTemplate: Firebase query completed in ${Math.round(loadTime)}ms with ${snapshot.size} documents`);
            
            // Process the data
            const rawScores = [];
            snapshot.forEach(doc => {
                const data = doc.data();
                console.log('🔍 Processing score document:', {id: doc.id, score: data.score, userUID: data.userUID, player: data.player});
                if (data.score && data.userUID) {
                    rawScores.push({
                        id: doc.id,
                        ...data,
                        timestamp: data.timestamp?.toDate() || new Date()
                    });
                }
            });

            console.log(`🔍 Raw scores processed: ${rawScores.length} from ${snapshot.size} documents`);

            // FOR HALL OF FAME: Show top scores regardless of user (not unique users)
            // This matches the template expectation of showing multiple entries
            this.leaderboardData = rawScores
                .sort((a, b) => b.score - a.score)
                .slice(0, 25); // Top 25 scores
            
            console.log(`🔍 Top scores selected: ${this.leaderboardData.length} (showing all scores, not unique users)`);
            
            // Also create unique users list for other purposes
            const userBestScores = {};
            rawScores.forEach(score => {
                if (!userBestScores[score.userUID] || score.score > userBestScores[score.userUID].score) {
                    userBestScores[score.userUID] = score;
                }
            });
            
            console.log(`🔍 Unique users found: ${Object.keys(userBestScores).length}`);

            console.log(`🏆 HallOfFameDesktopTemplate: Formatted ${this.leaderboardData.length} scores for display`);
            console.log('🔍 First few leaderboard entries:', this.leaderboardData.slice(0, 3));

            // Update the UI
            await this.updateInterface();
            
        } catch (error) {
            console.error('🏆 Error loading Hall of Fame data:', error);
            this.showErrorState();
        }
    }

    async updateInterface() {
        console.log('🏆 HallOfFameDesktopTemplate: Updating interface with data...');
        
        // Hide loading state
        const loadingElement = document.getElementById('hall-of-fame-loading');
        if (loadingElement) {
            loadingElement.style.display = 'none';
        }

        // Update user position
        this.updateUserPosition();
        
        // Update podium (top 3)
        this.updatePodium();
        
        // Update table
        this.updateTable();
        
        // Update stats
        this.updateStats();
        
        console.log('✅ HallOfFameDesktopTemplate: Interface updated successfully');
    }

    updateUserPosition() {
        console.log('🏆 HallOfFameDesktopTemplate: Updating user position...');
        console.log('🏆 Current user:', this.currentUser);
        console.log('🏆 Leaderboard data length:', this.leaderboardData.length);
        
        if (!this.currentUser) {
            console.warn('🏆 No current user - showing default position card');
            this.showDefaultPositionCard();
            return;
        }
        
        if (!this.leaderboardData.length) {
            console.warn('🏆 No leaderboard data - showing default position card');
            this.showDefaultPositionCard();
            return;
        }

        const userRank = this.leaderboardData.findIndex(item => item.userUID === this.currentUser.uid) + 1;
        const userScore = this.leaderboardData.find(item => item.userUID === this.currentUser.uid);

        console.log('🏆 User rank in leaderboard:', userRank);
        console.log('🏆 User score found:', userScore);

        if (userRank > 0 && userScore) {
            const positionCard = document.getElementById('user-position-card');
            if (positionCard) {
                positionCard.style.display = 'grid';
                
                // Update rank
                const rankElement = document.getElementById('user-rank');
                if (rankElement) rankElement.textContent = `#${userRank}`;
                
                // Update avatar
                const avatarElement = document.getElementById('user-avatar');
                if (avatarElement) {
                    const initials = this.currentUser.displayName.split(' ').map(n => n[0]).join('').toUpperCase();
                    avatarElement.textContent = initials;
                }
                
                // Update name
                const nameElement = document.getElementById('user-name');
                if (nameElement) nameElement.textContent = this.currentUser.displayName;
                
                // Update stats
                const pointsElement = document.getElementById('user-total-points');
                if (pointsElement) pointsElement.textContent = userScore.score.toLocaleString();
                
                const winRateElement = document.getElementById('user-win-rate');
                if (winRateElement) winRateElement.textContent = '85%'; // Placeholder
                
                const gamesElement = document.getElementById('user-games-played');
                if (gamesElement) gamesElement.textContent = '47'; // Placeholder
            }
        } else {
            console.warn('🏆 User not found in top 50 - showing default position');
            this.showDefaultPositionCard();
        }
    }

    showDefaultPositionCard() {
        console.log('🏆 Showing default position card...');
        const positionCard = document.getElementById('user-position-card');
        if (positionCard) {
            positionCard.style.display = 'grid';
            
            // Update with current user info or defaults
            const rankElement = document.getElementById('user-rank');
            if (rankElement) rankElement.textContent = '#1';
            
            const avatarElement = document.getElementById('user-avatar');
            if (avatarElement) {
                const initials = this.currentUser ? 
                    this.currentUser.displayName.split(' ').map(n => n[0]).join('').toUpperCase() : 'SH';
                avatarElement.textContent = initials;
            }
            
            const nameElement = document.getElementById('user-name');
            if (nameElement) {
                nameElement.textContent = this.currentUser ? this.currentUser.displayName : 'Shane Hercules';
            }
            
            // Set user profile info
            const companyElement = document.getElementById('user-company');
            if (companyElement) companyElement.textContent = '🏢 Bain & Company';
            
            const locationElement = document.getElementById('user-location');
            if (locationElement) locationElement.textContent = '📍 London';
            
            const titleElement = document.getElementById('user-title');
            if (titleElement) titleElement.textContent = '💼 Executive';
            
            // Set default stats
            const pointsElement = document.getElementById('user-total-points');
            if (pointsElement) pointsElement.textContent = '3,141';
            
            const winRateElement = document.getElementById('user-win-rate');
            if (winRateElement) winRateElement.textContent = '85%';
            
            const gamesElement = document.getElementById('user-games-played');
            if (gamesElement) gamesElement.textContent = '47';
            
            console.log('✅ Default position card displayed');
        }
    }

    updatePodium() {
        console.log('🏆 HallOfFameDesktopTemplate: Updating podium...');
        
        const podiumContainer = document.getElementById('podium-container');
        if (!podiumContainer || !this.leaderboardData.length) return;

        const top3 = this.leaderboardData.slice(0, 3);
        const podiumOrder = [1, 0, 2]; // Second, First, Third for visual layout
        
        podiumContainer.innerHTML = '';

        podiumOrder.forEach((index, position) => {
            const player = top3[index];
            if (!player) return;

            const place = index + 1;
            const className = place === 1 ? 'first' : place === 2 ? 'second' : 'third';
            const medalClass = place === 1 ? 'gold' : place === 2 ? 'silver' : 'bronze';
            
            const playerName = player.player || 'Anonymous';
            const initials = playerName.split(' ').map(n => n[0]).join('').toUpperCase();

            const podiumPlace = document.createElement('div');
            podiumPlace.className = `podium-place ${className}`;
            podiumPlace.innerHTML = `
                <div class="podium-medal ${medalClass}">${place}</div>
                <div class="podium-avatar">${initials}</div>
                <div class="podium-name">${playerName}</div>
                <div class="podium-company">${player.firm || 'Company'}</div>
                <div class="podium-score">${player.score.toLocaleString()}</div>
                <div class="podium-label">Points</div>
            `;
            
            podiumContainer.appendChild(podiumPlace);
        });

        console.log('✅ HallOfFameDesktopTemplate: Podium updated successfully');
    }

    updateTable() {
        console.log('🏆 HallOfFameDesktopTemplate: Updating table...');
        
        const tableBody = document.getElementById('leaderboard-table-body');
        if (!tableBody || !this.leaderboardData.length) return;

        tableBody.innerHTML = '';

        this.leaderboardData.slice(3).forEach((player, index) => {
            const rank = index + 4; // Start from rank 4
            const playerName = player.player || 'Anonymous';
            const initials = playerName.split(' ').map(n => n[0]).join('').toUpperCase();

            const row = document.createElement('tr');
            row.innerHTML = `
                <td class="rank-cell">${rank}</td>
                <td>
                    <div class="user-cell">
                        <div class="user-avatar">${initials}</div>
                        <div class="user-info">
                            <div class="user-name">${playerName}</div>
                            <div class="user-title">Player</div>
                        </div>
                    </div>
                </td>
                <td>${player.firm || 'Company'}</td>
                <td class="score-cell">${player.score.toLocaleString()}</td>
                <td>-</td>
                <td>-%</td>
                <td class="change-cell change-neutral">
                    <span>—</span>
                    <span>0</span>
                </td>
            `;
            
            tableBody.appendChild(row);
        });

        console.log('✅ HallOfFameDesktopTemplate: Table updated successfully');
    }

    updateStats() {
        // Update company rankings placeholder
        const companyRankings = document.getElementById('company-rankings');
        if (companyRankings) {
            companyRankings.innerHTML = `
                <div class="stat-item">
                    <span class="stat-name">Bain & Company</span>
                    <span class="stat-value">12,456</span>
                </div>
                <div class="stat-item">
                    <span class="stat-name">McKinsey & Company</span>
                    <span class="stat-value">11,234</span>
                </div>
                <div class="stat-item">
                    <span class="stat-name">BCG</span>
                    <span class="stat-value">10,987</span>
                </div>
                <div class="stat-item">
                    <span class="stat-name">Deloitte</span>
                    <span class="stat-value">9,654</span>
                </div>
            `;
        }
    }

    showErrorState() {
        const loadingElement = document.getElementById('hall-of-fame-loading');
        if (loadingElement) {
            loadingElement.innerHTML = `
                <div style="text-align: center; color: #ef4444;">
                    <div>Error loading Hall of Fame data</div>
                    <button onclick="location.reload()" style="margin-top: 16px; padding: 8px 16px; background: #00d4ff; border: none; border-radius: 8px; color: #0f172a; cursor: pointer;">
                        Retry
                    </button>
                </div>
            `;
        }
    }

    bindEvents() {
        console.log('🔗 HallOfFameDesktopTemplate: Binding events...');

        // Initialize with enterprise services (replaces duplicate event binding)
        baseSceneIntegration.initializeTemplateScene(this, {
            overlayId: 'auth-menu-dom-overlay',
            bindEvents: true,
            enableNavigation: true
        });

        // Filter events
        document.addEventListener('click', (e) => {
            if (e.target.classList.contains('filter-tab')) {
                // Update active filter tab
                document.querySelectorAll('.filter-tab').forEach(tab => tab.classList.remove('active'));
                e.target.classList.add('active');
                
                const filter = e.target.dataset.filter;
                console.log('🔍 Filter changed to:', filter);
                // TODO: Implement filtering logic
            }
            
            if (e.target.classList.contains('time-button')) {
                // Update active time button
                document.querySelectorAll('.time-button').forEach(btn => btn.classList.remove('active'));
                e.target.classList.add('active');
                
                const timeFilter = e.target.dataset.time;
                console.log('⏰ Time filter changed to:', timeFilter);
                // TODO: Implement time filtering logic
            }
        });

        // Search functionality
        const searchBox = document.getElementById('player-search');
        if (searchBox) {
            searchBox.addEventListener('input', (e) => {
                const searchTerm = e.target.value.toLowerCase();
                console.log('🔍 Search term:', searchTerm);
                // TODO: Implement search filtering
            });
        }

        console.log('🔗 HallOfFameDesktopTemplate: Events bound successfully');
    }

    // REMOVED: 37 lines of duplicate navigation logic
    // Now handled by baseSceneIntegration.initializeTemplateScene()
    // Navigation actions are centralized in NavigationActionHandler service

    shutdown() {
        console.log('[HallOfFameDesktopTemplate] Shutting down...');
        
        // Remove the overlay (SAME ID as Dashboard)
        const overlay = document.getElementById('auth-menu-dom-overlay');
        if (overlay) {
            overlay.remove();
        }
        
        // Show game container
        const gameContainer = document.getElementById('game-container');
        if (gameContainer) {
            gameContainer.style.display = 'block';
        }
        
        console.log('[HallOfFameDesktopTemplate] Shutdown complete.');
    }
}
